/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useCallback, useEffect, useMemo, useState, Fragment } from 'react';
import sortBy from 'lodash/sortBy';
import { BaseTemplate, Card, PageHead } from '@myob/myob-widgets';
import Breadcrumbs from 'components/Breadcrumbs';
import { ETelemetryNames } from 'telemetry/type';
import { MyBillsContainer } from 'modules/MyBills/styles';
import LatestBill from 'modules/MyBills/components/LatestBill';
import LoadingWrapper from 'components/LoadingWrapper';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'stores';
import { useHistory } from 'react-router-dom';
import { EActionNames, trackClickEventInMyBills, trackViewInMyBills } from 'modules/MyBills/myBillsHelper';
import { LocationStateInUnauthorizedPage } from 'pages/ErrorPages/Unauthorized/Unauthorized';
import { EHttpStatusCode } from 'helpers/type';
import { ERoutePath } from 'router';
import { EPage } from 'pages/ErrorPages/Unauthorized/pageContent';
import BillingHistory from 'modules/MyBills/components/BillingHistory/BillingHistory';
import { fetchBills, fetchMoreBills } from 'modules/MyBills/reducers';
import { IBill, IBillsState } from 'modules/MyBills';
import { ETableFields } from 'modules/MyBills/components/BillingHistory/helper';
import isEmpty from 'lodash/isEmpty';
import ErrorComponent from 'pages/ErrorPages/components/ErrorComponent/ErrorComponent';
import NavigationBar from 'components/NavigationBar';

export type TSortState = {
    activeSort: {
        column?: string;
        descending?: boolean;
    };
    data: IBill[];
};

const sortBills = (field: ETableFields, data: IBill[], descending: boolean) => {
    let result = data;
    switch (field) {
        case ETableFields.DateIssued:
            result = sortBy(data, bill => bill.invoiceDate);
            break;
        case ETableFields.BillingPeriod:
            result = sortBy(data, bill => bill.billingStartDate);
            break;
        case ETableFields.TotalAmount:
            result = sortBy(data, bill => Number(bill.totalAmount));
            break;
        case ETableFields.Status:
            result = sortBy(data, bill => bill.status);
            break;
    }
    return descending ? result.reverse() : result;
};
export const MyBills: React.FC = () => {
    const dispatch = useDispatch();
    const { push } = useHistory<LocationStateInUnauthorizedPage>();

    useEffect(() => {
        dispatch(
            fetchBills({
                page: 1,
                pageSize: 10,
            }),
        );
    }, [dispatch]);

    const { isLoading, history: billHistory, latest, error, isError } = useSelector<RootState, IBillsState>(
        state => state.bills,
    );

    const {
        data,
        meta: { totalRecords },
        isLoading: billHistoryLoading,
    } = billHistory;

    useEffect(() => {
        if (isLoading) return;

        if (!isError) {
            trackViewInMyBills('granted');
            return;
        }

        trackViewInMyBills('denied', error?.message);

        if (EHttpStatusCode.FORBIDDEN === error?.status) {
            push({
                pathname: ERoutePath.Unauthorized,
                state: {
                    pageName: EPage.MyBills,
                },
            });
        }
    }, [push, error, isLoading, isError]);

    const shouldShowBtn = totalRecords > data.length + 1;

    const initialState: TSortState = useMemo(
        () => ({
            activeSort: {},
            data,
        }),
        [data],
    );

    const [sortState, setSortState] = useState<TSortState>(initialState);

    useEffect(() => {
        setSortState({
            activeSort: {},
            data,
        });
    }, [data]);

    const onSort = useCallback(
        (column: ETableFields) => {
            const nextSortOrder =
                sortState.activeSort.column !== column || sortState.activeSort.descending == null
                    ? column !== ETableFields.DateIssued
                    : !sortState.activeSort.descending;
            setSortState({
                activeSort: { column, descending: nextSortOrder },
                data: sortBills(column, data, nextSortOrder),
            });
        },
        [data, sortState.activeSort],
    );

    const loadMore = () => {
        dispatch(
            fetchMoreBills({
                page: billHistory.meta.page + 1,
                pageSize: 10,
            }),
        );
        trackClickEventInMyBills(EActionNames.LoadMore, 'BillingHistory');
        setSortState({
            activeSort: {},
            data,
        });
    };

    return (
        <Fragment>
            <NavigationBar />
            <LoadingWrapper isLoading={isLoading}>
                <div data-testid="myBills">
                    <BaseTemplate>
                        <Breadcrumbs
                            parentPageName="Billing and payments"
                            parentPathname={ERoutePath.BillingAndPayments}
                            currentPageName="My bills"
                            telemetryEventName={ETelemetryNames.MyBills}
                        />
                        <MyBillsContainer>
                            <PageHead title="My bills" />
                            {!isError ? (
                                <Fragment>
                                    <Card body={<LatestBill latestBill={latest} />} />
                                    <BillingHistory
                                        bills={sortState.data}
                                        activeSort={sortState.activeSort}
                                        onSort={onSort}
                                        hasLatestBill={!isEmpty(latest)}
                                        loadMore={loadMore}
                                        shouldShowBtn={shouldShowBtn}
                                        disableLoadMoreBtn={billHistoryLoading}
                                    />
                                </Fragment>
                            ) : (
                                <ErrorComponent parentPage="MyBills" />
                            )}
                        </MyBillsContainer>
                    </BaseTemplate>
                </div>
            </LoadingWrapper>
        </Fragment>
    );
};
